import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Select,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "components-lib";
import { FiRefreshCcw } from "react-icons/fi";
import { useGlobalFilters } from "contexts/GlobalFiltersContext";
import { axiosInstance, API_ENDPOINTS, getAuthHeaders } from "config/api";
import {
  auditLogsColumnsData,
  loginActivityColumnsData,
  googleAuthLogsColumnsData,
} from "views/admin/default/variables/columnsData";
import { useAuditLogs } from "./hooks/useAuditLogs";
import { useLoginActivity } from "./hooks/useLoginActivity";
import { useGoogleAuthLogs } from "./hooks/useGoogleAuthLogs";

const ENTITY_TYPES = [
  "",
  "patient",
  "appointment",
  "bill",
  "payment",
  "doctor",
  "user",
  "clinic",
  "prescription",
  "module_connection",
  "system",
];

const ACTIONS = [
  "",
  "PATIENT_CREATE",
  "PATIENT_UPDATE",
  "PATIENT_DELETE",
  "APPOINTMENT_CREATE",
  "APPOINTMENT_UPDATE",
  "APPOINTMENT_STATUS_CHANGE",
  "APPOINTMENT_PAYMENT_ACTION",
  "APPOINTMENT_CANCEL",
  "APPOINTMENT_NO_SHOW",
  "APPOINTMENT_EMERGENCY",
  "APPOINTMENT_DELETE",
  "BILL_CREATE",
  "BILL_UPDATE",
  "BILL_DELETE",
  "BILL_INVOICE_UPLOAD",
  "PAYMENT_ADD",
  "PAYMENT_UPDATE",
  "PAYMENT_DELETE",
  "DOCTOR_CREATE",
  "DOCTOR_UPDATE",
  "DOCTOR_DELETE",
  "DOCTOR_PASSWORD_UPDATE",
  "FRONTDESK_CREATE",
  "FRONTDESK_UPDATE",
  "FRONTDESK_DELETE",
  "CLINIC_CREATE",
  "CLINIC_UPDATE",
  "CLINIC_LOCK",
  "CLINIC_UNLOCK",
  "PRESCRIPTION_CREATE",
  "PRESCRIPTION_UPDATE",
  "PRESCRIPTION_UPLOAD",
  "MODULE_CONNECT",
  "MODULE_DISCONNECT",
];

function renderCell(column, row) {
  const value = row[column.accessor];
  if (typeof column.Cell === "function") {
    return column.Cell({ value, row: { original: row } });
  }
  return value == null || value === "" ? "—" : String(value);
}

function SimpleDataTable({ columns, data, isLoading, onRowClick, emptyLabel }) {
  const headerBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.50");

  if (isLoading) {
    return (
      <Box py={8} textAlign="center">
        <Text color="secondaryGray.600">Loading…</Text>
      </Box>
    );
  }

  if (!data?.length) {
    return (
      <Box py={8} textAlign="center">
        <Text color="secondaryGray.600">{emptyLabel || "No records found"}</Text>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table variant="simple" size="sm">
        <Thead bg={headerBg}>
          <Tr>
            {columns.map((col) => (
              <Th key={col.accessor}>{col.Header}</Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, idx) => (
            <Tr
              key={row.id ?? idx}
              cursor={onRowClick ? "pointer" : "default"}
              _hover={onRowClick ? { bg: hoverBg } : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <Td key={col.accessor} maxW="280px" isTruncated>
                  {renderCell(col, row)}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function Pagination({ page, limit, total, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return (
    <Flex mt={4} align="center" justify="space-between" gap={3} flexWrap="wrap">
      <Text fontSize="sm" color="secondaryGray.600">
        {total} total · page {page} of {totalPages}
      </Text>
      <Flex gap={2}>
        <Button
          size="sm"
          variant="outline"
          isDisabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="outline"
          isDisabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </Flex>
    </Flex>
  );
}

export default function AuditLogs() {
  const toast = useToast();
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const cardBg = useColorModeValue("white", "navy.800");

  const tokenPayload = JSON.parse(localStorage.getItem("tokenPayload") || "{}");
  const isAdmin = String(tokenPayload?.role || "").toLowerCase() === "admin";

  const globalFiltersContext = useGlobalFilters();
  const { filters: globalFilters = {} } = globalFiltersContext || {};
  const gClinicId = globalFilters?.clinicId ?? null;

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [loginRole, setLoginRole] = useState("");
  const [googleSuccess, setGoogleSuccess] = useState("");
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const businessFilters = useMemo(
    () => ({
      from,
      to,
      entity_type: entityType,
      action,
      clinic_id: gClinicId || null,
      page,
      limit,
      enabled: isAdmin && tabIndex === 0,
    }),
    [from, to, entityType, action, gClinicId, page, limit, isAdmin, tabIndex],
  );

  const loginFilters = useMemo(
    () => ({
      from,
      to,
      role: loginRole,
      page,
      limit,
      enabled: isAdmin && tabIndex === 1,
    }),
    [from, to, loginRole, page, limit, isAdmin, tabIndex],
  );

  const googleFilters = useMemo(
    () => ({
      from,
      to,
      success: googleSuccess,
      page,
      limit,
      enabled: isAdmin && tabIndex === 2,
    }),
    [from, to, googleSuccess, page, limit, isAdmin, tabIndex],
  );

  const {
    logs,
    total: auditTotal,
    isLoading: auditLoading,
    refreshLogs,
  } = useAuditLogs(businessFilters);

  const {
    rows: loginRows,
    total: loginTotal,
    isLoading: loginLoading,
    refresh: refreshLogins,
  } = useLoginActivity(loginFilters);

  const {
    rows: googleRows,
    total: googleTotal,
    isLoading: googleLoading,
    refresh: refreshGoogle,
  } = useGoogleAuthLogs(googleFilters);

  const handleTabChange = useCallback((index) => {
    setTabIndex(index);
    setPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    if (tabIndex === 0) refreshLogs();
    else if (tabIndex === 1) refreshLogins();
    else refreshGoogle();
  }, [tabIndex, refreshLogs, refreshLogins, refreshGoogle]);

  const handleRowClick = useCallback(
    (row) => {
      setSelectedLog(row);
      onOpen();
    },
    [onOpen],
  );

  const handleExport = useCallback(async () => {
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;
      if (gClinicId) params.clinic_id = gClinicId;

      const response = await axiosInstance.get(API_ENDPOINTS.AUDIT.EXPORT, {
        headers: getAuthHeaders(),
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "audit_logs.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      toast({
        title: "Export failed",
        description: err.response?.data?.error || "Could not export audit logs.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    }
  }, [from, to, entityType, action, gClinicId, toast]);

  if (!isAdmin) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={4}>
        <Box bg={boxBg} borderRadius="16px" p={8} textAlign="center">
          <Text fontSize="lg" fontWeight="600" color="secondaryGray.900">
            Access denied
          </Text>
          <Text mt={2} color="secondaryGray.600">
            Audit Logs are available to admin users only.
          </Text>
        </Box>
      </Box>
    );
  }

  const activeTotal =
    tabIndex === 0 ? auditTotal : tabIndex === 1 ? loginTotal : googleTotal;

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={{ base: 3, md: 4 }}>
      <Flex
        mb={4}
        align="center"
        justify="space-between"
        flexWrap="wrap"
        gap={3}
      >
        <Text fontSize="2xl" fontWeight="700" color="secondaryGray.900">
          Audit Logs
        </Text>
        <Flex gap={2} align="center">
          {tabIndex === 0 && (
            <Button
              size="sm"
              colorScheme="brand"
              variant="outline"
              onClick={handleExport}
            >
              Export CSV
            </Button>
          )}
          <IconButton
            aria-label="Refresh"
            icon={<FiRefreshCcw />}
            size="sm"
            bg={boxBg}
            onClick={handleRefresh}
          />
        </Flex>
      </Flex>

      <Box bg={cardBg} borderRadius="16px" p={{ base: 3, md: 5 }} boxShadow="sm">
        <Flex gap={3} flexWrap="wrap" mb={4} align="flex-end">
          <Box>
            <Text fontSize="xs" mb={1} color="secondaryGray.600">
              From
            </Text>
            <Input
              type="date"
              size="sm"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              maxW="160px"
            />
          </Box>
          <Box>
            <Text fontSize="xs" mb={1} color="secondaryGray.600">
              To
            </Text>
            <Input
              type="date"
              size="sm"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              maxW="160px"
            />
          </Box>
          <Box>
            <Text fontSize="xs" mb={1} color="secondaryGray.600">
              Page size
            </Text>
            <Select
              size="sm"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              maxW="100px"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Select>
          </Box>

          {tabIndex === 0 && (
            <>
              <Box>
                <Text fontSize="xs" mb={1} color="secondaryGray.600">
                  Entity type
                </Text>
                <Select
                  size="sm"
                  value={entityType}
                  onChange={(e) => {
                    setEntityType(e.target.value);
                    setPage(1);
                  }}
                  maxW="180px"
                >
                  {ENTITY_TYPES.map((t) => (
                    <option key={t || "all"} value={t}>
                      {t || "All"}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text fontSize="xs" mb={1} color="secondaryGray.600">
                  Action
                </Text>
                <Select
                  size="sm"
                  value={action}
                  onChange={(e) => {
                    setAction(e.target.value);
                    setPage(1);
                  }}
                  maxW="220px"
                >
                  {ACTIONS.map((a) => (
                    <option key={a || "all"} value={a}>
                      {a || "All"}
                    </option>
                  ))}
                </Select>
              </Box>
            </>
          )}

          {tabIndex === 1 && (
            <Box>
              <Text fontSize="xs" mb={1} color="secondaryGray.600">
                Role
              </Text>
              <Select
                size="sm"
                value={loginRole}
                onChange={(e) => {
                  setLoginRole(e.target.value);
                  setPage(1);
                }}
                maxW="160px"
              >
                <option value="">All</option>
                <option value="admin">admin</option>
                <option value="doctor">doctor</option>
                <option value="front_desk">front_desk</option>
              </Select>
            </Box>
          )}

          {tabIndex === 2 && (
            <Box>
              <Text fontSize="xs" mb={1} color="secondaryGray.600">
                Success
              </Text>
              <Select
                size="sm"
                value={googleSuccess}
                onChange={(e) => {
                  setGoogleSuccess(e.target.value);
                  setPage(1);
                }}
                maxW="120px"
              >
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
            </Box>
          )}
        </Flex>

        <Tabs index={tabIndex} onChange={handleTabChange} variant="enclosed">
          <TabList>
            <Tab>Business audit</Tab>
            <Tab>Logins</Tab>
            <Tab>Google auth</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <SimpleDataTable
                columns={auditLogsColumnsData}
                data={logs}
                isLoading={auditLoading}
                onRowClick={handleRowClick}
                emptyLabel="No business audit events found"
              />
            </TabPanel>
            <TabPanel px={0}>
              <SimpleDataTable
                columns={loginActivityColumnsData}
                data={loginRows}
                isLoading={loginLoading}
                emptyLabel="No login activity found"
              />
            </TabPanel>
            <TabPanel px={0}>
              <SimpleDataTable
                columns={googleAuthLogsColumnsData}
                data={googleRows}
                isLoading={googleLoading}
                emptyLabel="No Google auth logs found"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>

        <Pagination
          page={page}
          limit={limit}
          total={activeTotal}
          onPageChange={setPage}
        />
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Audit event detail</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedLog && (
              <Box fontSize="sm">
                <Text mb={2}>
                  <Text as="span" fontWeight="600">
                    Action:{" "}
                  </Text>
                  {selectedLog.action}
                </Text>
                <Text mb={2}>
                  <Text as="span" fontWeight="600">
                    Summary:{" "}
                  </Text>
                  {selectedLog.summary || "—"}
                </Text>
                <Text mb={1} fontWeight="600">
                  Changes
                </Text>
                <Box
                  as="pre"
                  bg={boxBg}
                  p={3}
                  borderRadius="8px"
                  overflowX="auto"
                  mb={3}
                  whiteSpace="pre-wrap"
                >
                  {JSON.stringify(selectedLog.changes, null, 2) || "null"}
                </Box>
                <Text mb={1} fontWeight="600">
                  Metadata
                </Text>
                <Box
                  as="pre"
                  bg={boxBg}
                  p={3}
                  borderRadius="8px"
                  overflowX="auto"
                  whiteSpace="pre-wrap"
                >
                  {JSON.stringify(selectedLog.metadata, null, 2) || "null"}
                </Box>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
